import { KnownInboxLabel } from "@qw/qw-common";
import {
  type Options,
  parseAsArrayOf,
  parseAsBoolean,
  parseAsInteger,
  parseAsJson,
  parseAsString,
  useQueryStates,
} from "nuqs";
import { createContext, type FC, type PropsWithChildren, useCallback, useContext, useMemo } from "react";

export type DraftQueryPlacement = "floating" | "inline";

export interface DraftQueryMetadata {
  sourceConversationId?: string;
  placement?: DraftQueryPlacement;
}

const isDraftQueryPlacement = (value: unknown): value is DraftQueryPlacement =>
  value === "floating" || value === "inline";

const parseDraftMetadataByDraftId = (value: unknown): Record<string, DraftQueryMetadata> | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const metadataByDraftId: Record<string, DraftQueryMetadata> = {};

  for (const [draftId, rawMetadata] of Object.entries(value)) {
    if (!rawMetadata || typeof rawMetadata !== "object" || Array.isArray(rawMetadata)) {
      return null;
    }

    const rawEntry = rawMetadata as Record<string, unknown>;
    const entry: DraftQueryMetadata = {};

    if (rawEntry.sourceConversationId !== undefined) {
      if (typeof rawEntry.sourceConversationId !== "string") {
        return null;
      }

      if (rawEntry.sourceConversationId) {
        entry.sourceConversationId = rawEntry.sourceConversationId;
      }
    }

    if (rawEntry.placement !== undefined) {
      if (!isDraftQueryPlacement(rawEntry.placement)) {
        return null;
      }

      entry.placement = rawEntry.placement;
    }

    if (entry.sourceConversationId || entry.placement) {
      metadataByDraftId[draftId] = entry;
    }
  }

  return metadataByDraftId;
};

export interface InboxQueryStatesContextValue {
  conversationId: string | null;
  setConversationId: (conversationId: string | null, options?: Options) => void;
  chatSessionId: string | null;
  setChatSessionId: (chatSessionId: string | null, options?: Options) => void;
  clearChatSessionId: () => void;
  drafts: string[];
  setDrafts: (drafts: string[] | ((previousDrafts: string[]) => string[]), options?: Options) => void;
  draftMetadataByDraftId: Record<string, DraftQueryMetadata>;
  setDraftMetadataByDraftId: (
    metadata:
      | Record<string, DraftQueryMetadata>
      | ((previousMetadata: Record<string, DraftQueryMetadata>) => Record<string, DraftQueryMetadata>),
    options?: Options
  ) => void;
  openChat: boolean;
  clearOpenChat: () => void;
  searchTerm: string;
  setSearchTerm: (searchTerm: string, options?: Options) => void;
  /**
   * The committed `subject:` and `from:` operators, parsed out of the term the broker typed (see
   * `search/search-operators.ts`) and kept as params of their own rather than left in `?term=`.
   *
   * Params because they are scopes: `?subject=renewal` is bookmarkable, and the search box has to
   * render a chip for one wherever it loads — the `?unlinked=` precedent, whose absence meant a
   * bookmark holder got a silently narrowed inbox with no affordance to widen it. Keeping them out
   * of `?term=` is also what makes the box legible: the operator is a chip and the leftover free
   * text is what stays in the field.
   *
   * `""` is unset, so clearing one is `setSubjectSearch("")` — nuqs drops a param equal to its
   * default, the same way an emptied `?term=` disappears.
   */
  subjectSearch: string;
  setSubjectSearch: (subjectSearch: string, options?: Options) => void;
  senderSearch: string;
  setSenderSearch: (senderSearch: string, options?: Options) => void;
  /**
   * The whole search at once, because "clear the search" is one intent: the operators were typed
   * into the box like the rest of the term, so leaving them behind hands the next search a scope
   * the broker thought they had just thrown away.
   *
   * The scopes set from somewhere other than the box are left alone — `?unlinked=` has its own
   * control, and the insured scope is the route.
   */
  clearSearch: () => void;
  unlinked: boolean;
  setUnlinked: (unlinked: boolean, options?: Options) => void;
  labels: string[];
  setLabels: (labels: string[], options?: Options) => void;
  notLabels: string[];
  setNotLabels: (notLabels: string[], options?: Options) => void;
  page: number;
  setPage: (page: number, options?: Options) => void;
}

const InboxQueryStatesContext = createContext<InboxQueryStatesContextValue | null>(null);

// NOTE: This provider is used to manage the query state for the inbox application.
export const InboxQueryStatesProvider: FC<PropsWithChildren> = ({ children }) => {
  const [queryStates, setQueryStates] = useQueryStates({
    conversationId: parseAsString,
    chatSessionId: parseAsString,
    drafts: parseAsArrayOf(parseAsString).withDefault([]),
    draftMetadata: parseAsJson<Record<string, DraftQueryMetadata>>(parseDraftMetadataByDraftId).withDefault({}),
    openChat: parseAsBoolean.withDefault(false),
    term: parseAsString.withDefault(""),
    // Named for the operator rather than the server field, because the URL is the thing a broker
    // reads and shares: `?subject=renewal&from=sarah` says back what they typed.
    subject: parseAsString.withDefault(""),
    from: parseAsString.withDefault(""),
    unlinked: parseAsBoolean.withDefault(false),
    labels: parseAsArrayOf(parseAsString).withDefault([KnownInboxLabel.INBOX]),
    notLabels: parseAsArrayOf(parseAsString).withDefault([KnownInboxLabel.TRASH]),
    page: parseAsInteger.withDefault(1),
  });

  const conversationId = queryStates.conversationId;
  const setConversationId = useCallback(
    (nextConversationId: string | null, options?: Options) =>
      setQueryStates({ conversationId: nextConversationId }, { history: "push", ...options }),
    [setQueryStates]
  );

  const drafts = queryStates.drafts;
  const setDrafts = useCallback(
    (nextDrafts: string[] | ((previousDrafts: string[]) => string[]), options?: Options) =>
      setQueryStates((prevStates) => {
        return { drafts: typeof nextDrafts === "function" ? nextDrafts(prevStates.drafts) : nextDrafts };
      }, options),
    [setQueryStates]
  );

  const draftMetadataByDraftId = queryStates.draftMetadata;
  const setDraftMetadataByDraftId = useCallback(
    (
      nextMetadata:
        | Record<string, DraftQueryMetadata>
        | ((previousMetadata: Record<string, DraftQueryMetadata>) => Record<string, DraftQueryMetadata>),
      options?: Options
    ) =>
      setQueryStates((prevStates) => {
        return {
          draftMetadata: typeof nextMetadata === "function" ? nextMetadata(prevStates.draftMetadata) : nextMetadata,
        };
      }, options),
    [setQueryStates]
  );

  const openChat = queryStates.openChat;
  const clearOpenChat = useCallback(() => setQueryStates({ openChat: null }, { history: "replace" }), [setQueryStates]);

  const chatSessionId = queryStates.chatSessionId;
  const setChatSessionId = useCallback(
    (next: string | null, options?: Options) => setQueryStates({ chatSessionId: next }, options),
    [setQueryStates]
  );
  const clearChatSessionId = useCallback(
    () => setQueryStates({ chatSessionId: null }, { history: "replace" }),
    [setQueryStates]
  );

  const searchTerm = queryStates.term;
  const setSearchTerm = useCallback(
    (nextSearchTerm: string, options?: Options) => setQueryStates({ term: nextSearchTerm, page: 1 }, options),
    [setQueryStates]
  );

  // Both reset the page for the same reason `setSearchTerm` does: an operator changes which
  // conversations exist, so an offset into the previous result set means nothing.
  const subjectSearch = queryStates.subject;
  const setSubjectSearch = useCallback(
    (nextSubjectSearch: string, options?: Options) => setQueryStates({ subject: nextSubjectSearch, page: 1 }, options),
    [setQueryStates]
  );

  const senderSearch = queryStates.from;
  const setSenderSearch = useCallback(
    (nextSenderSearch: string, options?: Options) => setQueryStates({ from: nextSenderSearch, page: 1 }, options),
    [setQueryStates]
  );

  const clearSearch = useCallback(() => setQueryStates({ term: "", subject: "", from: "", page: 1 }), [setQueryStates]);

  const unlinked = queryStates.unlinked;
  const setUnlinked = useCallback(
    (nextUnlinked: boolean, options?: Options) => setQueryStates({ unlinked: nextUnlinked }, options),
    [setQueryStates]
  );

  const labels = useMemo(() => {
    // IMPORTANT: We need to convert the `ALL_MAIL` label to an empty array for the conversations query.
    if (queryStates.labels?.includes(KnownInboxLabel.ALL_MAIL)) {
      return [];
    }

    return queryStates.labels;
  }, [queryStates.labels]);
  const setLabels = useCallback(
    (nextLabels: string[], options?: Options) => setQueryStates({ labels: nextLabels }, options),
    [setQueryStates]
  );

  const notLabels = queryStates.notLabels;
  const setNotLabels = useCallback(
    (nextNotLabels: string[], options?: Options) => setQueryStates({ notLabels: nextNotLabels }, options),
    [setQueryStates]
  );

  const page = queryStates.page;
  const setPage = useCallback(
    (nextPage: number, options?: Options) => setQueryStates({ page: nextPage }, options),
    [setQueryStates]
  );

  const value: InboxQueryStatesContextValue = useMemo(
    () => ({
      conversationId,
      setConversationId,
      chatSessionId,
      setChatSessionId,
      clearChatSessionId,
      drafts,
      setDrafts,
      draftMetadataByDraftId,
      setDraftMetadataByDraftId,
      openChat,
      clearOpenChat,
      searchTerm,
      setSearchTerm,
      subjectSearch,
      setSubjectSearch,
      senderSearch,
      setSenderSearch,
      clearSearch,
      unlinked,
      setUnlinked,
      labels,
      setLabels,
      notLabels,
      setNotLabels,
      page,
      setPage,
    }),
    [
      conversationId,
      setConversationId,
      chatSessionId,
      setChatSessionId,
      clearChatSessionId,
      drafts,
      setDrafts,
      draftMetadataByDraftId,
      setDraftMetadataByDraftId,
      openChat,
      clearOpenChat,
      searchTerm,
      setSearchTerm,
      subjectSearch,
      setSubjectSearch,
      senderSearch,
      setSenderSearch,
      clearSearch,
      unlinked,
      setUnlinked,
      labels,
      setLabels,
      notLabels,
      setNotLabels,
      page,
      setPage,
    ]
  );

  return <InboxQueryStatesContext.Provider value={value}>{children}</InboxQueryStatesContext.Provider>;
};

export const useInboxQueryStates = () => {
  const context = useContext(InboxQueryStatesContext);

  if (!context) {
    throw new Error("useInboxQueryStates must be used within an InboxQueryStatesProvider");
  }

  return context;
};
